import {
  assetKey, validateAssetDescriptor,
  type AssetDescriptor, type AssetLease, type AssetLocation, type AssetRequestOptions,
  type AssetResolver, type AssetStore,
} from './AssetContracts';
import { PreparationScheduler } from './PreparationScheduler';

export const DEFAULT_ASSET_BUDGET_BYTES = 256 * 1024 * 1024;

interface Download {
  readonly controller: AbortController;
  readonly promise: Promise<ArrayBuffer>;
  readonly scope: string | null;
  consumers: number;
}

export interface AssetLoaderOptions {
  readonly budgetBytes?: number;
  readonly fetch?: typeof fetch;
  readonly now?: () => number;
}

export class AssetLoader {
  readonly #downloads = new Map<string, Download>();
  readonly #pins = new Map<string, number>();
  readonly #resident = new Map<string, { bytes: ArrayBuffer; scope: string | null }>();
  readonly #scopeLifetimes = new Map<string, AbortController>();
  readonly #lifetime = new AbortController();
  #residentBytes = 0;
  readonly #leases = new Map<AssetLease, string | null>();
  readonly #network = new PreparationScheduler(4);
  readonly #management = new PreparationScheduler();
  readonly #fetch: typeof fetch;
  readonly #now: () => number;
  readonly #budget: number;
  #disposed = false;

  constructor(readonly store: AssetStore, options: AssetLoaderOptions = {}) {
    this.#fetch = options.fetch ?? ((...args) => fetch(...args));
    this.#now = options.now ?? Date.now;
    this.#budget = options.budgetBytes ?? DEFAULT_ASSET_BUDGET_BYTES;
    if (!Number.isSafeInteger(this.#budget) || this.#budget < 0) throw new Error('Invalid asset cache budget.');
  }

  async acquire(descriptor: AssetDescriptor, resolver: AssetResolver, options: AssetRequestOptions = {}): Promise<AssetLease> {
    validateAssetDescriptor(descriptor);
    if (descriptor.access.kind === 'private') {
      const scope = descriptor.access.scope;
      let lifetime = this.#scopeLifetimes.get(scope);
      if (!lifetime) { lifetime = new AbortController(); this.#scopeLifetimes.set(scope, lifetime); }
      const signal = AbortSignal.any([this.#lifetime.signal, lifetime.signal, ...(options.signal ? [options.signal] : [])]);
      signal.throwIfAborted();
      const location = await resolver.resolve(descriptor, signal);
      signal.throwIfAborted();
      assertLocationValid(location, this.#now());
      return this.#acquire(descriptor, { resolve: async () => location }, { ...options, signal }, location);
    }
    return this.#acquire(descriptor, resolver, options);
  }

  #acquire(descriptor: AssetDescriptor, resolver: AssetResolver, options: AssetRequestOptions, authorization?: AssetLocation): Promise<AssetLease> {
    validateAssetDescriptor(descriptor);
    if (this.#disposed) return Promise.reject(new Error('Asset loader is disposed.'));
    if (options.signal?.aborted) return Promise.reject(options.signal.reason);
    const key = assetKey(descriptor);
    const scope = descriptor.access.kind === 'private' ? descriptor.access.scope : null;
    let download = this.#downloads.get(key);
    if (!download || download.controller.signal.aborted) {
      const controller = new AbortController();
      const promise = this.#network.schedule(signal => this.#load(descriptor, resolver, signal),
        { signal: controller.signal, priority: options.priority });
      download = { controller, promise, scope, consumers: 0 };
      this.#downloads.set(key, download);
      const current = download;
      void promise.finally(() => {
        if (this.#downloads.get(key) === current) this.#downloads.delete(key);
      }).catch(() => undefined);
    }
    const shared = download;
    shared.consumers += 1;
    this.#pins.set(key, (this.#pins.get(key) ?? 0) + 1);
    return new Promise<AssetLease>((resolve, reject) => {
      let waiting = true;
      const detach = () => {
        waiting = false;
        options.signal?.removeEventListener('abort', abort);
        shared.consumers -= 1;
        if (shared.consumers === 0) shared.controller.abort();
      };
      const abort = () => {
        if (!waiting) return;
        detach();
        this.#unpin(key);
        reject(options.signal?.reason);
      };
      options.signal?.addEventListener('abort', abort, { once: true });
      void shared.promise.then(bytes => {
        if (!waiting) return;
        if (shared.controller.signal.aborted || this.#disposed) {
          detach(); this.#unpin(key); reject(new DOMException('Asset acquisition cancelled.', 'AbortError')); return;
        }
        detach();
        const lease = createAssetLease(bytes.slice(0), descriptor.mimeType, () => {
          this.#leases.delete(lease);
          this.#unpin(key);
        }, authorization ? () => assertLocationValid(authorization, this.#now()) : undefined);
        this.#leases.set(lease, scope);
        resolve(lease);
      }, error => {
        if (!waiting) return;
        detach(); this.#unpin(key); reject(error);
      });
    });
  }

  /** For local previews or unmanifested resources: no persistence or integrity claim. */
  async acquireUnverified(location: AssetLocation, options: AssetRequestOptions = {}): Promise<AssetLease> {
    if (this.#disposed) throw new Error('Asset loader is disposed.');
    return this.#network.schedule(async signal => {
      const response = await this.#download(location, signal);
      const bytes = await readBoundedResponse(response, 128 * 1024 * 1024, signal);
      signal.throwIfAborted();
      assertLocationValid(location, this.#now());
      const lease = createAssetLease(bytes, response.headers.get('content-type')?.split(';')[0] ?? 'application/octet-stream',
        () => { this.#leases.delete(lease); }, () => assertLocationValid(location, this.#now()));
      this.#leases.set(lease, null);
      return lease;
    }, options);
  }

  async purgePrivateScope(scope?: string): Promise<void> {
    const matches = (candidate: string | null) => candidate !== null && (scope === undefined || candidate === scope);
    for (const [owner, lifetime] of this.#scopeLifetimes) {
      if (matches(owner)) { lifetime.abort(); this.#scopeLifetimes.delete(owner); }
    }
    for (const [key, entry] of this.#resident) if (matches(entry.scope)) this.#forgetResident(key);
    for (const download of this.#downloads.values()) if (matches(download.scope)) download.controller.abort();
    for (const [lease, owner] of this.#leases) if (matches(owner)) lease.release();
    await this.#manage(async () => {
      const records = await this.store.records();
      for (const record of records) if (matches(record.scope)) await this.store.remove(record.key);
    });
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#lifetime.abort();
    this.#resident.clear();
    this.#residentBytes = 0;
    for (const download of this.#downloads.values()) download.controller.abort();
    this.#network.dispose();
    for (const lease of this.#leases.keys()) lease.release();
    this.#downloads.clear();
  }

  async #load(descriptor: AssetDescriptor, resolver: AssetResolver, signal: AbortSignal): Promise<ArrayBuffer> {
    const key = assetKey(descriptor);
    // Private acquisition already obtained a fresh authorization before joining this work.
    const location = descriptor.access.kind === 'private' ? await resolver.resolve(descriptor, signal) : null;
    signal.throwIfAborted();
    if (location) assertLocationValid(location, this.#now());
    const resident = this.#resident.get(key);
    if (resident) {
      this.#resident.delete(key);
      this.#resident.set(key, resident);
      return resident.bytes;
    }
    const cached = await this.store.read(key).catch(() => null);
    if (cached) {
      if (await verifyAssetBytes(cached, descriptor)) {
        signal.throwIfAborted();
        if (location) assertLocationValid(location, this.#now());
        this.#remember(key, cached, descriptor);
        void this.#manage(() => this.store.touch(key, this.#now())).catch(() => undefined);
        return cached;
      }
      await this.#manage(() => this.store.remove(key)).catch(() => undefined);
    }
    const downloadLocation = location ?? await resolver.resolve(descriptor, signal);
    const response = await this.#download(downloadLocation, signal);
    const bytes = await readBoundedResponse(response, descriptor.byteLength, signal);
    signal.throwIfAborted();
    if (!await verifyAssetBytes(bytes, descriptor)) throw new Error('Asset integrity verification failed.');
    assertLocationValid(downloadLocation, this.#now());
    await this.#manage(async () => {
      signal.throwIfAborted();
      const records = await this.store.records();
      const estimate: { quota?: number; usage?: number } | undefined = await this.store.estimate?.().catch(() => ({}));
      const storedBytes = records.reduce((total, record) => total + record.byteLength, 0);
      const quota = estimate?.quota;
      const available = quota === undefined ? this.#budget
        : Math.max(0, quota * 0.8 - Math.max(0, (estimate?.usage ?? 0) - storedBytes));
      const budget = Math.min(this.#budget, available);
      if (bytes.byteLength > budget) return;
      const alreadyStored = records.find(record => record.key === key);
      let used = storedBytes - (alreadyStored?.byteLength ?? 0);
      for (const record of [...records].sort((left, right) => left.lastAccess - right.lastAccess)) {
        if (used + bytes.byteLength <= budget) break;
        if (this.#pins.has(record.key)) continue;
        await this.store.remove(record.key);
        used -= record.byteLength;
      }
      signal.throwIfAborted();
      if (used + bytes.byteLength > budget) return;
      await this.store.write({ key, byteLength: bytes.byteLength, lastAccess: this.#now(),
        scope: descriptor.access.kind === 'private' ? descriptor.access.scope : null }, bytes);
      // A purge/dispose during a platform write must not leave private bytes published.
      if (signal.aborted) await this.store.remove(key);
    }).catch(() => undefined); // Storage denial/quota does not prevent verified playback.
    signal.throwIfAborted();
    assertLocationValid(downloadLocation, this.#now());
    this.#remember(key, bytes, descriptor);
    return bytes;
  }

  #manage<T>(operation: () => Promise<T>): Promise<T> {
    return this.#management.schedule(() => this.store.withExclusiveLock ? this.store.withExclusiveLock(operation) : operation());
  }

  #forgetResident(key: string): void {
    const entry = this.#resident.get(key);
    if (entry) this.#residentBytes -= entry.bytes.byteLength;
    this.#resident.delete(key);
  }

  #remember(key: string, bytes: ArrayBuffer, descriptor: AssetDescriptor): void {
    const budget = Math.min(this.#budget, 32 * 1024 * 1024);
    if (bytes.byteLength > budget) return;
    this.#forgetResident(key);
    while (this.#residentBytes + bytes.byteLength > budget && this.#resident.size) {
      this.#forgetResident(this.#resident.keys().next().value!);
    }
    this.#resident.set(key, { bytes, scope: descriptor.access.kind === 'private' ? descriptor.access.scope : null });
    this.#residentBytes += bytes.byteLength;
  }

  async #download(location: AssetLocation, signal: AbortSignal): Promise<Response> {
    assertLocationValid(location, this.#now());
    const response = await this.#fetch(location.url, { signal });
    if (!response.ok) throw new Error(`Asset request failed with HTTP ${response.status}.`);
    return response;
  }

  #unpin(key: string): void {
    const remaining = (this.#pins.get(key) ?? 1) - 1;
    if (remaining <= 0) this.#pins.delete(key);
    else this.#pins.set(key, remaining);
  }
}

export function assertLocationValid(location: AssetLocation, now = Date.now()): void {
  if (typeof location.url !== 'string' || !location.url
    || (location.expiresAt !== undefined && (!Number.isFinite(location.expiresAt) || location.expiresAt <= now))) {
    throw new Error('Asset download authorization is expired or invalid.');
  }
  const url = new URL(location.url, 'https://assets.invalid');
  if (!['https:', 'http:', 'blob:'].includes(url.protocol) || url.username || url.password) {
    throw new Error('Invalid asset transport URL.');
  }
}

export async function verifyAssetBytes(bytes: ArrayBuffer, descriptor: AssetDescriptor): Promise<boolean> {
  if (bytes.byteLength !== descriptor.byteLength) return false;
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  return Array.from(digest, value => value.toString(16).padStart(2, '0')).join('') === descriptor.sha256;
}

function createAssetLease(bytes: ArrayBuffer, mimeType: string, onRelease: () => void, checkAuthorization?: () => void): AssetLease {
  let objectUrl: string | null = null;
  let released = false;
  return {
    readBytes: async () => {
      if (released) throw new Error('Asset lease is released.');
      checkAuthorization?.();
      return bytes;
    }, mimeType,
    objectUrl: () => {
      if (released) throw new Error('Asset lease is released.');
      checkAuthorization?.();
      objectUrl ??= URL.createObjectURL(new Blob([bytes], { type: mimeType }));
      return objectUrl;
    },
    release: () => {
      if (released) return;
      released = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      onRelease();
    },
  };
}

async function readBoundedResponse(response: Response, maximumBytes: number, signal: AbortSignal): Promise<ArrayBuffer> {
  const declared = Number(response.headers.get('content-length'));
  if (Number.isFinite(declared) && declared > maximumBytes) {
    await response.body?.cancel();
    throw new Error('Asset response exceeds its declared size.');
  }
  if (!response.body) {
    const bytes = await response.arrayBuffer();
    if (bytes.byteLength > maximumBytes) throw new Error('Asset response exceeds its declared size.');
    return bytes;
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let length = 0;
  const cancel = () => { void reader.cancel(signal.reason).catch(() => undefined); };
  signal.addEventListener('abort', cancel, { once: true });
  try {
    for (;;) {
      signal.throwIfAborted();
      const chunk = await reader.read();
      signal.throwIfAborted();
      if (chunk.done) break;
      length += chunk.value.byteLength;
      if (length > maximumBytes) {
        await reader.cancel();
        throw new Error('Asset response exceeds its declared size.');
      }
      chunks.push(chunk.value);
    }
    const bytes = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    return bytes.buffer;
  } finally { signal.removeEventListener('abort', cancel); reader.releaseLock(); }
}
